import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const inputCls =
  "rounded-none border border-foreground/20 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-sm font-light";

export default function FaqsTab() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/content/faqs")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load FAQs"));
  }, []);

  const set = (i, k, v) => setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setItems((xs) => [...xs, { q: "", a: "" }]);
  const remove = (i) => setItems((xs) => xs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/faqs", items.filter((x) => x.q && x.a));
      setItems(r.data);
      toast.success("FAQs saved — live on the website now.");
    } catch {
      toast.error("Could not save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="faqs-tab">
      <div className="flex items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">
          FAQs <span className="text-foreground/40 text-2xl">({items.length})</span>
        </h2>
        <div className="flex gap-3">
          <button onClick={add} data-testid="faq-add" className="btn-sharp border border-foreground/30 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em]">
            + Add
          </button>
          <Button onClick={save} disabled={saving} data-testid="faqs-save" className="btn-sharp rounded-none border border-primary bg-primary text-primary-foreground hover:bg-primary px-6 py-2.5 text-[10px] uppercase tracking-[0.2em]">
            {saving ? "Saving…" : "Save all"}
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {items.map((f, i) => (
          <div key={i} className="border border-foreground/15 p-5" data-testid={`faq-item-edit-${i}`}>
            <div className="flex justify-between items-start gap-3 mb-3">
              <span className="text-[10px] uppercase tracking-[0.24em] text-foreground/50">Question {i + 1}</span>
              <button onClick={() => remove(i)} data-testid={`faq-remove-${i}`} className="text-foreground/40 hover:text-destructive text-lg leading-none">×</button>
            </div>
            <Input value={f.q} onChange={(e) => set(i, "q", e.target.value)} placeholder="Question" className={inputCls} data-testid={`faq-q-${i}`} />
            <Textarea value={f.a} onChange={(e) => set(i, "a", e.target.value)} placeholder="Answer" rows={3} className={`${inputCls} mt-3`} data-testid={`faq-a-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
